import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@legal-500/legal500-design-system";
import { contacts } from "@/api/contacts.service";
import type { Contact } from "@/api/contacts.service";
import styles from "../../../pages/Profiles/ProfilesForm.module.css";

export type AddressBookContactPickerProps = {
  /** Address-book company UUID the contact search is scoped to. */
  companyId: string | null;
  /** Currently-selected address-book contact UUID. */
  value: string | null;
  onChange: (contactId: string | null) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  label?: string;
  /**
   * What a missing `companyId` means. Off (the default, the Profiles form): the
   * picker is disabled until a firm is chosen, because a profile's contact must
   * belong to its firm. On (the awards list filter): the picker searches the
   * whole Address Book, and a `companyId` only narrows the search to that firm's
   * roster.
   */
  companyOptional?: boolean;
  /** Placeholder of the enabled input. */
  placeholder?: string;
};

const DEBOUNCE_MS = 300;
// Typeahead page size. Bounded (the contacts endpoint accepts up to 500) but
// large enough to show every contact for a typical firm on open — firms with
// ~50 contacts were previously truncated to the first 10.
const RESULT_LIMIT = 50;

/** The name a contact is shown under: the Address Book's full name, or first
 * and last name when that is blank. */
export function contactLabel(contact: Contact): string {
  return contact.name?.trim() || `${contact.firstName ?? ""} ${contact.lastName ?? ""}`.trim();
}

/**
 * Single-select typeahead for an address-book contact.
 *
 * Mirrors `CompanyPicker`: debounced search against the contacts list endpoint
 * (filtered by the firm's address-book company UUID when there is one),
 * resolves the currently-selected contact via `contacts.getById`, and emits the
 * selected contact UUID — which is exactly the `contactId` profiles-api
 * requires.
 *
 * By default the picker is disabled until a firm is chosen, because a profile's
 * contact search must be scoped to a single firm. With `companyOptional` it
 * searches the whole Address Book instead, and a firm, when one is given, only
 * narrows the search to that firm's roster.
 */
export function AddressBookContactPicker({
  companyId,
  value,
  onChange,
  required,
  disabled,
  error,
  label = "Address book contact",
  companyOptional = false,
  placeholder = "Search contacts…",
}: AddressBookContactPickerProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [results, setResults] = useState<Contact[]>([]);
  const [resolvedName, setResolvedName] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isDisabled = disabled || (!companyOptional && !companyId);

  // Debounce query.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Search contacts, scoped to the selected firm when there is one. `companyId`
  // is the firm's address-book company UUID, which the contacts endpoint
  // accepts directly. With a firm, an empty box is a real search: the firm's
  // whole roster. Without one (only reachable with `companyOptional`) an empty
  // box searches nothing, because the first page of the whole Address Book is
  // not anyone's roster — so the picker waits for a term instead.
  useEffect(() => {
    const search = debouncedQuery.trim();
    if (isDisabled || (!companyId && !search)) {
      setResults([]);
      // A search cancelled by the previous run's cleanup never clears its own
      // `loading` (its `finally` sees `cancelled`), and with the box emptied
      // no later search would either — the open list would sit on
      // "Searching…" for good.
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    contacts
      .getAll({
        companyId: companyId ?? undefined,
        search: search || undefined,
        limit: RESULT_LIMIT,
      })
      .then(resp => {
        if (!cancelled) setResults(resp.contacts);
      })
      .catch(err => {
        // Search failure shouldn't break the picker — surface to dev console
        // so the failure is debuggable without disrupting the UI.
        console.error("[AddressBookContactPicker] contact search failed", debouncedQuery, err);
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, companyId, isDisabled]);

  // Resolve the currently-selected contact's display name. `value` is a UUID.
  useEffect(() => {
    if (!value) {
      setResolvedName(null);
      return;
    }
    let cancelled = false;
    contacts
      .getById(value)
      .then(contact => {
        if (!cancelled) setResolvedName(contact ? contactLabel(contact) : null);
      })
      .catch(err => {
        // Resolution failure shouldn't break the picker — surface to dev
        // console so the failure is debuggable without disrupting the UI.
        console.error("[AddressBookContactPicker] failed to resolve contact", value, err);
        if (!cancelled) setResolvedName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  // Close on outside click.
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const displayValue = useMemo(() => {
    if (open) return query;
    if (value && resolvedName) return resolvedName;
    if (value) return `Contact ${value}`;
    return query;
  }, [open, query, value, resolvedName]);

  const inputPlaceholder = companyId || companyOptional ? placeholder : "Select a firm first";

  const select = (contact: Contact) => {
    onChange(contact.id);
    setQuery("");
    setOpen(false);
    setResolvedName(contactLabel(contact));
  };

  return (
    <div ref={containerRef} className={styles.comboboxContainer}>
      <Input
        label={required ? `${label} *` : label}
        type="text"
        value={displayValue}
        placeholder={inputPlaceholder}
        disabled={isDisabled}
        onFocus={() => setOpen(true)}
        onChange={e => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        aria-label={label}
        aria-required={required}
        role="combobox"
        aria-expanded={open}
        fullWidth
      />
      {value && !isDisabled && (
        <button
          type="button"
          aria-label="Clear contact"
          className={styles.inlineClear}
          onClick={() => {
            onChange(null);
            setQuery("");
            setResolvedName(null);
          }}
        >
          ×
        </button>
      )}
      {open && !isDisabled && (results.length > 0 || loading) && (
        <ul role="listbox" className={styles.comboboxList}>
          {loading && <li className={styles.comboboxStatus}>Searching…</li>}
          {!loading &&
            results.map(contact => (
              <li
                key={contact.id}
                role="option"
                aria-selected={contact.id === value}
                tabIndex={0}
                onClick={() => select(contact)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    select(contact);
                  }
                }}
                className={styles.comboboxItem}
              >
                <span className={styles.comboboxItemPrimary}>{contactLabel(contact)}</span>
                {contact.email && <span className={styles.comboboxItemMeta}>{contact.email}</span>}
              </li>
            ))}
        </ul>
      )}
      {error && (
        <p role="alert" className={styles.fieldError}>
          {error}
        </p>
      )}
    </div>
  );
}

export default AddressBookContactPicker;
